import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Divider,
  Stack,
} from "@mui/material";
import {
  Share2,
  ArrowLeft,
} from "lucide-react";
import { Editor } from "react-draft-wysiwyg";
import { useHistory } from "react-router-dom";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import { Path } from "../../../../../static/Constants";
import { CommonButton } from "../../../../Components/ui";

const NewsDetailsContent = ({
  article,
  editorState,
  onShare,
}) => {
  const history = useHistory();
  if (!article) return null;

  const {
    title,
    imageUrl,
  } = article;

  return (
    <Box sx={{ width: "100%" }}>
      {/* Featured Cover Image */}
      {imageUrl ? (
        <Box
          sx={{
            width: "100%",
            maxHeight: { xs: 260, sm: 420 },
            borderRadius: borderRadiusPx.xl,
            overflow: "hidden",
            mb: 4,
            bgcolor: colors.slate[100],
            border: `1px solid ${colors.dashboard.cardBorder}`,
            boxShadow: customShadows.cardFlat,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            component="img"
            src={imageUrl}
            alt={title}
            sx={{
              width: "100%",
              height: "100%",
              maxHeight: { xs: 260, sm: 420 },
              objectFit: "cover",
              display: "block",
            }}
          />
        </Box>
      ) : null}

      {/* Article Body Content Card */}
      <Card
        sx={{
          p: { xs: 3, sm: 4.5 },
          borderRadius: borderRadiusPx.xl,
          backgroundColor: colors.dashboard.cardBg,
          border: `1px solid ${colors.dashboard.cardBorder}`,
          boxShadow: customShadows.cardFlat,
          mb: 4,
        }}
      >
        {/* Rich Text Editor Render Body */}
        <Box
          className="news-details-body"
          sx={{
            fontSize: "1.05rem",
            color: colors.slate[700],
            lineHeight: 1.8,
            "& .rdw-editor-main": {
              p: 0,
            },
            "& p": {
              mb: 2.25,
            },
            "& h1, & h2, & h3, & h4": {
              fontWeight: 800,
              color: colors.slate[900],
              mt: 3,
              mb: 1.5,
              lineHeight: 1.3,
            },
            "& h1": { fontSize: "1.75rem" },
            "& h2": { fontSize: "1.5rem" },
            "& h3": { fontSize: "1.25rem" },
            "& a": {
              color: colors.primary.main,
              textDecoration: "underline",
              fontWeight: 600,
            },
            "& ul, & ol": {
              pl: 3.5,
              mb: 2.5,
            },
            "& li": {
              mb: 1,
            },
            "& blockquote": {
              borderLeft: `4px solid ${colors.primary.main}`,
              pl: 2.5,
              py: 1,
              my: 3,
              fontStyle: "italic",
              bgcolor: colors.blue[50],
              color: colors.slate[800],
              borderRadius: `0 ${borderRadiusPx.md} ${borderRadiusPx.md} 0`,
            },
            "& code": {
              px: 1,
              py: 0.25,
              borderRadius: borderRadiusPx.xs,
              bgcolor: colors.slate[100],
              color: colors.primary.dark,
              fontSize: "0.9em",
              fontFamily: "monospace",
            },
          }}
        >
          {editorState ? (
            <Editor editorState={editorState} toolbarHidden readOnly />
          ) : (
            <Typography variant="body1">No content available</Typography>
          )}
        </Box>

        <Divider sx={{ my: 3.5, borderColor: colors.dashboard.tableBorder }} />

        {/* Footer Actions & Share Row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          {/* <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="caption" sx={{ fontWeight: 700, color: colors.slate[400], textTransform: "uppercase" }}>
              Category:
            </Typography>
            <CommonChip label="Official" size="small" sx={{ bgcolor: colors.blue[50], color: colors.primary.main, fontWeight: 600 }} />
            <CommonChip label="Autobet" size="small" sx={{ bgcolor: colors.slate[100], color: colors.slate[700], fontWeight: 500 }} />
            <CommonChip label="Ecosystem" size="small" sx={{ bgcolor: colors.slate[100], color: colors.slate[700], fontWeight: 500 }} />
          </Stack> */}

          <Stack direction="row" spacing={1.5} alignItems="center">
            <CommonButton
              variant="outline"
              size="small"
              startIcon={<ArrowLeft size={14} />}
              onClick={() => history.push(Path.news)}
            >
              All Articles
            </CommonButton>

            <CommonButton
              variant="primary"
              size="small"
              startIcon={<Share2 size={14} />}
              onClick={onShare}
            >
              Share Article
            </CommonButton>
          </Stack>
        </Box>
      </Card>
    </Box>
  );
};

NewsDetailsContent.propTypes = {
  article: PropTypes.object,
  editorState: PropTypes.object,
  onShare: PropTypes.func.isRequired,
};

export default NewsDetailsContent;
